import React from 'react';
import { motion } from 'framer-motion';
import { GraduationCap, Award, Users, ChevronRight } from 'lucide-react';
import { AllKpiData } from '../../hooks/useKommunData';
import { kpiMeta, getDeltaBgColor } from '../../config/kpi-config';
import { formatValue } from '../../utils/formatters';

interface KommunSummaryHeroProps {
    allKpiData: AllKpiData;
    latestYear: number;
    onKpiSelect: (kpiId: string) => void;
    loading?: boolean;
}

const KommunSummaryHero: React.FC<KommunSummaryHeroProps> = ({
    allKpiData,
    latestYear,
    onKpiSelect,
    loading = false
}) => {
    // const isDesktop = useIsDesktop(); // Unused for now
    // Define key KPIs for the hero section with their icons and National Benchmarks (Riket)
    // Benchmarks are approximated baselines (could be fetched dynamically in future)
    const HERO_KPIS = [
        { id: 'N15504', label: 'Meritvärde', icon: GraduationCap, riket: 228.4 },
        { id: 'N15501', label: 'Behörighet Gymnasiet', icon: Award, riket: 84.2 },
        { id: 'N15813', label: 'Lärarbehörighet', icon: Users, riket: 70.8 }
    ];

    if (loading) {
        return (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="h-40 bg-slate-100 dark:bg-slate-800 animate-pulse rounded-[2rem] border border-slate-200 dark:border-slate-700" />
                ))}
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {HERO_KPIS.map((item, index) => {
                const data = allKpiData[item.id];
                if (!data || data.length === 0) return null;

                const latestEntry = data.find(d => d.year === latestYear) || data[data.length - 1];
                const value = latestEntry.value;
                const delta = latestEntry.prevYearValue !== null ? value - latestEntry.prevYearValue : 0;
                const riketValue = latestEntry.riksValue ?? item.riket; // Dynamic with hardcoded fallback
                const meta = kpiMeta[item.id];

                if (!meta) return null;

                const deltaColorClass = getDeltaBgColor(delta, meta.higherIsBetter);
                const Icon = item.icon;

                // Calculate percentages for progress bars
                const maxVal = meta.type === 'poäng' ? 340 : 100;
                const currentPercent = (value / maxVal) * 100;
                const riketPercent = (riketValue / maxVal) * 100;

                const isBetterThanRiket = meta.higherIsBetter ? value > riketValue : value < riketValue;

                return (
                    <motion.button
                        key={item.id}
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.15, duration: 0.6, ease: [0.23, 1, 0.32, 1] }}
                        whileHover={{ y: -6, boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.15)' }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => onKpiSelect(item.id)}
                        className="group relative flex flex-col justify-between h-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-7 rounded-[2.5rem] text-left transition-all duration-300 shadow-sm"
                    >
                        {/* Glossy Overlay Decor */}
                        <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-emerald-50/5 to-emerald-50/10 dark:via-white/0 dark:to-white/[0.02] rounded-[2.5rem] pointer-events-none" />

                        <div className="relative z-10 w-full">
                            <div className="flex justify-between items-start mb-6">
                                <div className={`p-3 rounded-2xl bg-gradient-to-br ${isBetterThanRiket ? 'from-emerald-500/15 to-emerald-600/25 text-emerald-600' : 'from-slate-500/15 to-slate-600/25 text-slate-600'} dark:from-emerald-900/40 dark:to-emerald-800/10 dark:text-emerald-400 ring-1 ring-emerald-500/10 group-hover:scale-110 transition-transform`}>
                                    <Icon className="w-6 h-6" />
                                </div>
                                <div className="flex flex-col items-end gap-1">
                                    {latestEntry.prevYearValue !== null && Math.abs(delta) >= 0.05 && (
                                        <div className={`text-[10px] font-black px-2.5 py-1 rounded-full uppercase tracking-widest ${deltaColorClass} shadow-sm border border-emerald-500/10`}>
                                            {delta > 0 ? '↑' : '↓'} {Math.abs(delta).toFixed(1)} {meta.deltaUnit}
                                        </div>
                                    )}
                                    <span className={`text-[9px] font-black uppercase tracking-tighter ${isBetterThanRiket ? 'text-emerald-600' : 'text-slate-500'}`}>
                                        {isBetterThanRiket ? 'Över Riket' : 'Under Riket'}
                                    </span>
                                </div>
                            </div>

                            <div>
                                <h3 className="text-[11px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em] mb-2 opacity-80 flex items-center gap-1">
                                    {meta.shortName || item.label}
                                    <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity translate-x-0 group-hover:translate-x-1" />
                                </h3>
                                <div className="flex items-baseline gap-2">
                                    <span className="text-5xl font-black text-slate-900 dark:text-white tabular-nums tracking-tighter transition-colors group-hover:text-emerald-600 dark:group-hover:text-emerald-400">
                                        {formatValue(value, meta).replace(' p', '').replace(' %', '')}
                                    </span>
                                    <span className="text-sm text-slate-400 dark:text-slate-500 font-bold tracking-widest">
                                        {meta.unit}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Premium Progress Section */}
                        <div className="mt-8 relative w-full">
                            <div className="relative h-2 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden mb-2">
                                <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: `${currentPercent}%` }}
                                    transition={{ duration: 1.2, delay: 0.5, ease: [0.23, 1, 0.32, 1] }}
                                    className={`absolute inset-y-0 left-0 ${isBetterThanRiket ? 'bg-emerald-500 dark:bg-emerald-600' : 'bg-slate-400 dark:bg-slate-500'} rounded-full shadow-[0_0_12px_rgba(16,185,129,0.4)]`}
                                />
                                {/* National Average Marker Line */}
                                <div
                                    className="absolute inset-y-0 w-0.5 bg-slate-900 dark:bg-white z-10 shadow-[0_0_4px_rgba(255,255,255,0.8)]"
                                    style={{ left: `${riketPercent}%` }}
                                    title="Riket"
                                />
                            </div>

                            <div className="flex justify-between items-center text-[9px] text-slate-400 uppercase font-black tracking-widest opacity-60">
                                <span>Start</span>
                                <span className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded text-[8px] text-slate-900 dark:text-white">Riket: {riketValue.toFixed(1)}</span>
                                <span>Max {maxVal}</span>
                            </div>
                        </div>
                    </motion.button>
                );
            })}
        </div>
    );
};

export default KommunSummaryHero;
